/**
 * Production-safe entry point for mock-auth.
 *
 * In production builds (`import.meta.env.PROD === true`), Rollup
 * statically proves the dynamic-import branch is dead-code and
 * tree-shakes it; the prod bundle contains no mock-auth code.
 *
 * However, this module is **also** retained in production builds
 * when the runtime-config explicitly enables demo mode
 * (`authMode=mock` AND `apiBaseUrl` points at `localhost` /
 * `127.0.0.1`). The `runtime-config-reload` module calls
 * `setMockAuthDemoMode(true)` when it detects this combination so
 * the demo container image can ship with mock-auth available
 * without weakening tenant clusters (which point at real
 * ZITADEL issuers).
 */

export interface MockAuthExports {
  /** Storage key for the persisted mock user. */
  k: string;
  /** listMockUsers(): MockAuthUser[] */
  l: () => unknown[];
  /** getMockUserByRole(role) */
  g: (role: string) => unknown;
  /** loadPersistedMockUser() */
  p: () => unknown;
  /** persistMockUser(user) */
  s: (user: unknown) => void;
  /** signInMockAsRole(role) */
  i: (role: string) => unknown;
  /** syntheticMockToken(user) */
  t: (user: unknown) => string;
}

/** Build-time, not runtime. See `vite.config.ts`. When false the branch
 *  below is statically taken, so Rollup can see the dynamic import is
 *  unreachable and the mock-auth chunk is never emitted. */
declare const __ALLOW_MOCK_AUTH__: boolean;

let cached: Promise<MockAuthExports> | null = null;
/**
 * Demo-mode flag. Set by `runtime-config-reload` when the runtime
 * config identifies a local-only deployment (authMode=mock +
 * apiBaseUrl=localhost). When true, `loadMockAuth()` is allowed
 * in production builds so the docker demo container works. When
 * false (default), mock-auth is strictly dev-only.
 */
let demoMode = false;

/**
 * Test helper: turn demo-mode on/off without touching the env.
 */
export function setMockAuthDemoMode(enabled: boolean): void {
  demoMode = enabled;
}

export function isMockAuthAvailable(): boolean {
  if (!__ALLOW_MOCK_AUTH__) return false;
  return !import.meta.env.PROD || demoMode;
}

export function loadMockAuth(): Promise<MockAuthExports> {
  // TWO GATES, and they are different promises.
  //
  // The build-time one is the real guarantee: a production artefact does not
  // CONTAIN the code, so no runtime-config can talk it into minting a token.
  // The runtime one still stands behind it, so even a demo build refuses
  // unless it is pointed at localhost.
  if (!__ALLOW_MOCK_AUTH__) {
    return Promise.reject(
      new Error(
        'mock-auth is not built into this artefact. ' +
          'Set runtime-config.json authMode=zitadel for production tenants.',
      ),
    );
  }
  if (import.meta.env.PROD && !demoMode) {
    return Promise.reject(
      new Error(
        'mock-auth is not available in production builds. ' +
          'Set runtime-config.json authMode=zitadel for production tenants.',
      ),
    );
  }
  if (!cached) {
    cached = import('./mock-auth').then((m) => ({
      k: m.MOCK_AUTH_STORAGE_KEY,
      l: m.listMockUsers as never,
      g: m.getMockUserByRole as never,
      p: m.loadPersistedMockUser as never,
      s: m.persistMockUser as never,
      i: m.signInMockAsRole as never,
      t: m.syntheticMockToken as never,
    }));
  }
  return cached;
}

/** Synchronous reset for tests; never call from production code. */
export function __resetMockAuthCacheForTests(): void {
  cached = null;
  demoMode = false;
}
